/**
 * Client-side archive of past Manager-app conversations. The live conversation
 * stays in `pa-chat-history-<uid>`; when the user starts a new chat (or restores
 * an old one) the current thread is pushed here so nothing is lost.
 */
export type ArchivedChat = { id: string; title: string; messages: any[]; savedAt: number };

const MAX = 50;
const archiveKey = (uid: string) => `pa-chat-archive-${uid}`;

export function loadArchive(uid: string): ArchivedChat[] {
  try {
    const raw = localStorage.getItem(archiveKey(uid));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch { return []; }
}

export function saveArchive(uid: string, list: ArchivedChat[]): void {
  try { localStorage.setItem(archiveKey(uid), JSON.stringify(list.slice(0, MAX))); } catch { /* ignore */ }
}

/** Push the given conversation to the front of the archive (no-op if empty). */
export function archiveChat(uid: string, messages: any[]): void {
  if (!Array.isArray(messages) || messages.length === 0) return;
  const firstUser = messages.find((m: any) => m?.role === 'user');
  const title = String(firstUser?.content || 'Conversation').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Conversation';
  const list = loadArchive(uid);
  // Skip if the last archived thread is identical (same length + same first msg).
  const top = list[0];
  if (top && top.messages?.length === messages.length && top.title === title) return;
  list.unshift({ id: `chat-${messages.length}-${title.length}-${(messages[messages.length - 1]?.content || '').length}-${Math.round((Date.now ? Date.now() : 0))}`, title, messages, savedAt: Date.now ? Date.now() : 0 });
  saveArchive(uid, list);
}

export function deleteArchived(uid: string, id: string): ArchivedChat[] {
  const next = loadArchive(uid).filter((c) => c.id !== id);
  saveArchive(uid, next);
  return next;
}
